import { useAuth } from '@/components/AuthProvider';
import {
  canAccessDashboard,
  canAccessSettings,
  canAccessUsers,
  canPerformAction,
  getFeatureAccess,
  canAccessVirtualTerminal
} from '@/lib/permissions';
import { UserRole } from '@/types/firebase';

/**
 * Hook to provide permission check methods across the application
 * Simplified to focus on menu-based permissions - users either see a menu item or they don't
 */
export function usePermissions() {
  const { user, userRole } = useAuth();

  return {
    // Menu access permissions - these control whether menu items are visible
    canAccessDashboard: () => canAccessDashboard(user, userRole),
    canAccessSettings: () => canAccessSettings(user, userRole),
    canViewUsers: () => canAccessUsers(user, userRole),
    
    // Invoices menu access
    canViewInvoices: () => canPerformAction(user, 'invoices', 'canView', userRole),
    canCreateInvoice: () => canPerformAction(user, 'invoices', 'canCreate', userRole),
    canEditInvoice: () => canPerformAction(user, 'invoices', 'canEdit', userRole),
    canDeleteInvoice: () => canPerformAction(user, 'invoices', 'canDelete', userRole),
    
    // Customers menu access
    canViewCustomers: () => canPerformAction(user, 'customers', 'canView', userRole),
    canCreateCustomer: () => canPerformAction(user, 'customers', 'canCreate', userRole),
    canEditCustomer: () => canPerformAction(user, 'customers', 'canEdit', userRole),
    canDeleteCustomer: () => canPerformAction(user, 'customers', 'canDelete', userRole),
    
    // Products menu access
    canViewProducts: () => canPerformAction(user, 'products', 'canView', userRole),
    canCreateProduct: () => canPerformAction(user, 'products', 'canCreate', userRole),
    canEditProduct: () => canPerformAction(user, 'products', 'canEdit', userRole),
    canDeleteProduct: () => canPerformAction(user, 'products', 'canDelete', userRole),
    
    // Transactions menu access
    canViewTransactions: () => canPerformAction(user, 'transactions', 'canView', userRole),
    canCreateTransaction: () => canPerformAction(user, 'transactions', 'canCreate', userRole),
    canEditTransaction: () => canPerformAction(user, 'transactions', 'canEdit', userRole),
    canDeleteTransaction: () => canPerformAction(user, 'transactions', 'canDelete', userRole),
    
    // Virtual Terminal permission (for menu access)
    canAccessVirtualTerminal: () => canAccessVirtualTerminal(user, userRole),
    
    // Get access level for a feature (for completeness)
    getAccess: (feature: 'invoices' | 'customers' | 'products' | 'transactions' | 'users') => 
      getFeatureAccess(user, feature, userRole),
      
    // Role-based permission check
    hasPermission: ({ requiredRole, allowHigherRoles = true }: { requiredRole: UserRole, allowHigherRoles?: boolean }) => {
      if (!user) return false;
      
      // Get the user's role
      const userRoleString = user.role;
      
      const roleHierarchy: { [key in UserRole]: number } = {
        'super_admin': 100,
        'admin': 80,
        'manager': 60,
        'user': 20
      };
      
      if (allowHigherRoles) {
        return roleHierarchy[userRoleString] >= roleHierarchy[requiredRole];
      }
      
      return userRoleString === requiredRole;
    },

    // Donations menu access
    canViewDonations: () => canPerformAction(user, 'donations', 'canView', userRole),
    canCreateDonation: () => canPerformAction(user, 'donations', 'canCreate', userRole),
    canEditDonation: () => canPerformAction(user, 'donations', 'canEdit', userRole),
    canDeleteDonation: () => canPerformAction(user, 'donations', 'canDelete', userRole),
  };
} 